import type {SessionState} from '@github-ui/agent-sessions/types/session'

import type {AgentTask} from '../../api/memex-items/contracts'
import type {MemexItemModel} from '../../models/memex-item-model'

export function shouldShowAgentTaskPill(state: SessionState | undefined): state is SessionState {
  if (!state) return false
  return true
}

export const MAX_AGENT_TASK_SUB_ROWS = 5

export function isUnhydratedAgentTask(task: AgentTask): boolean {
  return !!task.id && !task.state && !task.hydrationFailed
}

export function isVisibleAgentTask(task: AgentTask): boolean {
  if (shouldShowAgentTaskPill(task.state)) return true
  return isUnhydratedAgentTask(task)
}

export function isProgressTask(task: AgentTask): boolean {
  return (
    task.state === 'in_progress' ||
    task.state === 'queued' ||
    task.state === 'waiting_for_user' ||
    task.state === 'idle'
  )
}

export function getVisibleAgentTaskSubRows(model: MemexItemModel): Array<AgentTask> {
  const tasks = model.agentTasks ?? []

  // Filter before capping so non-rendered unhydrated tasks cannot consume sub-row slots.
  const visible = tasks.filter(task => shouldShowAgentTaskPill(task.state))

  if (visible.length <= MAX_AGENT_TASK_SUB_ROWS) {
    return visible
  }

  const inProgress = visible.filter(isProgressTask)
  const completed = visible.filter(t => !isProgressTask(t))

  return [...inProgress, ...completed].slice(0, MAX_AGENT_TASK_SUB_ROWS)
}

export function getLatestAgentTaskState(tasks: Array<AgentTask> | undefined): SessionState | null {
  if (!tasks || tasks.length === 0) return null

  const latest = tasks.reduce((a, b) => (new Date(b.createdAt ?? 0) > new Date(a.createdAt ?? 0) ? b : a))
  return latest.state ?? null
}
